import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Select, Tabs } from 'antd';
import { Calendar } from 'antd-mobile';
import TaskList from '../employee/TaskList';
import EmployeesList from './EmployeesList';
import { useAuth, returnGrops } from '../../Hooks/UserHooks';
import { fetchAdminTaskList, fetchEmployeesList } from '../../fetch/fetch';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';

const { TabPane } = Tabs;

const Manage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('tasks');
  const { userProfile } = useAuth();
  const [onlyActiveRange, setOnlyActiveRange] = useState<{ start: string; end: string } | undefined>(undefined);
  const [onlyActiveDrawerOpen, setOnlyActiveDrawerOpen] = useState(false);

  const [taskOptions, setTaskOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [employeeOptions, setEmployeeOptions] = useState<Array<{ label: string; value: string }>>([]);

  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(undefined);
  const [selectedEmployeeUid, setSelectedEmployeeUid] = useState<string | undefined>(undefined);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  let auth = '';
  let jwtToken = '';
  if (userProfile) {
    auth = returnGrops(userProfile);
    jwtToken = userProfile.signInUserSession.accessToken.jwtToken;
  }

  const userInitials = useMemo(() => {
    const username = String((userProfile as any)?.username || '').trim();
    const fromProfile = username ? username.slice(0, 2).toUpperCase() : '';
    const fromStorage = typeof window !== 'undefined' ? localStorage.getItem('userInitials') : '';
    return fromProfile || fromStorage || 'AP';
  }, [userProfile]);

  useEffect(() => {
    setSelectedTaskId(undefined);
    setSelectedEmployeeUid(undefined);
    setOnlyActiveRange(undefined);
    setOnlyActiveDrawerOpen(false);

    const loadOptions = async () => {
      if (!jwtToken) return;
      if (activeTab === 'tasks') {
        const res = await fetchAdminTaskList(jwtToken);
        if (res?.status === '1' && Array.isArray(res.response)) {
          const opts = res.response.map((t: any) => ({
            value: String(t.task_id ?? ''),
            label: String(t.title ?? t.comments ?? t.task_id ?? ''),
          }));
          setTaskOptions(opts.filter((o: any) => o.value));
        } else {
          setTaskOptions([]);
        }
      } else if (activeTab === 'employees') {
        const res = await fetchEmployeesList(jwtToken);
        if (res?.status === '1' && Array.isArray(res.response)) {
          const opts = res.response.map((e: any) => ({
            value: String(e.uid ?? ''),
            label: String(e.name ?? e.email ?? e.uid ?? ''),
          }));
          setEmployeeOptions(opts.filter((o: any) => o.value));
        } else {
          setEmployeeOptions([]);
        }
      }
    };

    loadOptions();
  }, [activeTab, jwtToken]);

  const selectOptions = activeTab === 'tasks' ? taskOptions : employeeOptions;
  const selectValue = activeTab === 'tasks' ? selectedTaskId : selectedEmployeeUid;
  const selectPlaceholder = activeTab === 'tasks' ? 'Project name' : 'Employee name';

  const handleSelectChange = (value: string) => {
    if (activeTab === 'tasks') {
      setSelectedTaskId(value);
      setSelectedEmployeeUid(undefined);
    } else {
      setSelectedEmployeeUid(value);
      setSelectedTaskId(undefined);
    }
  };

  const handleClear = () => {
    setSelectedTaskId(undefined);
    setSelectedEmployeeUid(undefined);
  };

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const defaultOnlyActiveRange = useMemo(() => {
    const end = moment().startOf('day').format('YYYY-MM-DD');
    const start = moment().startOf('day').subtract(2, 'day').format('YYYY-MM-DD');
    return { start, end };
  }, []);

  const handleOnlyActiveCalendarChange = (value: any) => {
    if (value && value.length === 2) {
      const start = moment(value[0]).format('YYYY-MM-DD');
      const end = moment(value[1]).format('YYYY-MM-DD');
      setOnlyActiveRange({ start, end });
    }
  };

  return (
    <div className="manage-page">
      {/* Tabs container */}
      <div
        className="manage-topbar"
        style={{
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 900,
          margin: '0 auto',
          zIndex: 1000,
          padding: 18,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #FFF 0%, rgba(251, 251, 247, 0.60) 100%)',
          borderRadius: '0 0 32px 32px',
          boxShadow: '0 8px 30px 0 rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: '100%',
          }}
        >
          <div
            className="manage-topbar-row"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              className="manage-avatar"
              style={{
                width: 40,
                height: 40,
                borderRadius: 9999,
                background: '#F0F2F5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                fontSize: 12,
                fontWeight: 600,
                flex: '0 0 auto',
              }}
            >
              {userInitials}
            </div>

            <Select
              className="manage-search-select"
              showSearch
              allowClear
              placeholder={selectPlaceholder}
              value={selectValue}
              onChange={handleSelectChange}
              onClear={handleClear}
              options={selectOptions}
              style={{ flex: 1, minWidth: 220 }}
              suffixIcon={!selectValue ? <SearchOutlined style={{ color: '#6b7280' }} /> : null}
              size="large"
              dropdownClassName="manage-search-select-dropdown"
              filterOption={(input, option) => {
                const label = String(option?.label ?? '');
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            />

            <div
              className="manage-only-active"
              role="button"
              tabIndex={0}
              onClick={() => {
                setOnlyActiveRange((prev) => prev ?? defaultOnlyActiveRange);
                setOnlyActiveDrawerOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOnlyActiveRange((prev) => prev ?? defaultOnlyActiveRange);
                  setOnlyActiveDrawerOpen(true);
                }
              }}
            >
              <span className="manage-only-active-text">Only active</span>
              <svg
                className="manage-only-active-icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ display: 'block' }}
              >
                <path
                  d="M13.125 11.292C13.6697 11.292 14.1986 11.4736 14.6289 11.8076C15.0592 12.1416 15.3668 12.609 15.502 13.1367L15.5107 13.1719H16.875C17.0283 13.1719 17.1758 13.2324 17.2842 13.3408C17.3926 13.4492 17.4531 13.5967 17.4531 13.75C17.4531 13.9033 17.3926 14.0507 17.2842 14.1592C17.1758 14.2676 17.0283 14.3281 16.875 14.3281H15.5107L15.502 14.3633C15.3668 14.8909 15.0592 15.3584 14.6289 15.6924C14.1986 16.0264 13.6697 16.208 13.125 16.208C12.5803 16.208 12.0514 16.0264 11.6211 15.6924C11.1908 15.3584 10.8832 14.8909 10.748 14.3633L10.7393 14.3281H3.125C2.97167 14.3281 2.82424 14.2676 2.71582 14.1592C2.60741 14.0507 2.54688 13.9033 2.54688 13.75C2.54688 13.5967 2.60741 13.4492 2.71582 13.3408C2.82424 13.2324 2.97168 13.1719 3.125 13.1719H10.7393L10.748 13.1367C10.8832 12.609 11.1908 12.1416 11.6211 11.8076C12.0514 11.4736 12.5803 11.292 13.125 11.292ZM12.8721 12.4785C12.6205 12.5285 12.3894 12.6516 12.208 12.833C12.0266 13.0144 11.9036 13.2455 11.8535 13.4971C11.8035 13.7485 11.8287 14.0092 11.9268 14.2461C12.0249 14.483 12.1911 14.6856 12.4043 14.8281C12.6176 14.9706 12.8685 15.0469 13.125 15.0469C13.469 15.0469 13.7988 14.9102 14.042 14.667C14.2852 14.4238 14.4219 14.0939 14.4219 13.75C14.4219 13.4935 14.3456 13.2425 14.2031 13.0293C14.0606 12.8161 13.858 12.6499 13.6211 12.5517C13.3842 12.4537 13.1236 12.4285 12.8721 12.4785ZM8.125 3.79197C8.66969 3.79197 9.19862 3.97361 9.62891 4.3076C10.0592 4.64159 10.3668 5.10902 10.502 5.6367L10.5107 5.67186H16.875C17.0283 5.67186 17.1758 5.73239 17.2842 5.8408C17.3926 5.94922 17.4531 6.09666 17.4531 6.24998C17.4531 6.4033 17.3926 6.55074 17.2842 6.65916C17.1758 6.76758 17.0283 6.82811 16.875 6.82811H10.5107L10.502 6.86326C10.3668 7.39093 10.0592 7.85837 9.62891 8.19236C9.19862 8.52636 8.6697 8.70799 8.125 8.70799C7.5803 8.70799 7.05138 8.52636 6.62109 8.19236C6.19081 7.85837 5.88315 7.39093 5.74805 6.86326L5.73926 6.82811H3.125C2.97167 6.82811 2.82424 6.76758 2.71582 6.65916C2.60741 6.55074 2.54688 6.4033 2.54688 6.24998C2.54688 6.09666 2.60741 5.94922 2.71582 5.8408C2.82424 5.73239 2.97168 5.67186 3.125 5.67186H5.73926L5.74805 5.6367C5.88315 5.10902 6.19081 4.64159 6.62109 4.3076C7.05138 3.97361 7.58031 3.79197 8.125 3.79197ZM8.125 4.95311C7.78105 4.95311 7.45122 5.08978 7.20801 5.33299C6.9648 5.5762 6.82813 5.90604 6.82812 6.24998C6.82812 6.50648 6.90437 6.75741 7.04688 6.97068C7.18937 7.18385 7.39201 7.3501 7.62891 7.44822C7.86578 7.54629 8.12648 7.57242 8.37793 7.52244C8.6295 7.4724 8.86062 7.34834 9.04199 7.16697C9.22336 6.9856 9.34645 6.75448 9.39648 6.50291C9.44651 6.25141 9.42134 5.99081 9.32324 5.75389C9.22512 5.51699 9.05887 5.31435 8.8457 5.17186C8.63243 5.02935 8.3815 4.95311 8.125 4.95311Z"
                  fill="#0E1F27"
                  stroke="#0E1F27"
                  strokeWidth="0.09375"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <Drawer
        title="Select Date Range"
        width={400}
        placement="right"
        closable={true}
        onClose={() => setOnlyActiveDrawerOpen(false)}
        visible={onlyActiveDrawerOpen}
      >
        <Calendar
          selectionMode="range"
          onChange={handleOnlyActiveCalendarChange}
          style={
            {
              '--adm-color-primary': '#0E1F27',
            } as React.CSSProperties
          }
          value={
            onlyActiveRange
              ? [moment(onlyActiveRange.start).toDate(), moment(onlyActiveRange.end).toDate()]
              : undefined
          }
          max={today}
        />
        <Button
          size="large"
          type="primary"
          style={{
            marginTop: 10,
            background: '#0E1F27',
            borderColor: '#0E1F27',
          }}
          onClick={() => setOnlyActiveDrawerOpen(false)}
        >
          Confirm
        </Button>
      </Drawer>
      <div
        style={{
          paddingTop: 102,
          maxWidth: 900,
          margin: '0 auto',
          width: '100%',
          paddingLeft: 18,
          paddingRight: 18,
          paddingBottom: 18,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ padding: '20px', maxWidth: '760px', margin: '0 auto', width: '100%' }}>

          <Tabs
            className="manage-tabs"
            activeKey={activeTab}
            onChange={handleTabChange}
            tabBarGutter={0}
          >
            <TabPane
              tab={<span className="manage-tab-label">Projects</span>}
              key="tasks"
            />
            <TabPane
              tab={<span className="manage-tab-label">Employees</span>}
              key="employees"
            />
          </Tabs>
        </div>

        {activeTab === 'tasks' && (
          <TaskList
            auth={auth}
            jwtToken={jwtToken}
            isManage={true}
            selectedTaskId={selectedTaskId}
            activeDateRange={onlyActiveRange}
          />
        )}
        {activeTab === 'employees' && (
          <EmployeesList jwtToken={jwtToken} selectedEmployeeUid={selectedEmployeeUid} />
        )}
      </div>
    </div>
  );
};

export default Manage;
